import type { Config } from '@packages/config';
import type { FormatNumberOptions, IntlFormatters } from 'react-intl';
import { logger } from '../logger/logger';

/**
 * method to format a price value as float (in euros) to a customized price string based on the provided price format, locale and currency
 *
 * @param price the price value as Integer (in cents) which is the shop default
 * @param currency the currency for the price value
 * @param priceFormat the price format to be used for formatting the price value, usually provided by the config "priceFormat" property
 * @param locale the locale to be used for formatting the price value
 * @param formatNumber formatNumber method from react-intl IntlFormatters, used for fallback formatting and as part of custom formatting
 * @returns string
 */

export const formatPrice = (
  price: number,
  currency: FormatNumberOptions['currency'],
  priceFormat: Config['priceFormat'],
  locale: string,
  formatNumber: IntlFormatters['formatNumber'],
) => {
  switch (priceFormat) {
    /* 
        Empiriecom price format has ISO 
          delimiters (character at thousands), 
          Euro value,
          fraction (the character between Euro and Cent),
          Cent value,
          minusSign
      */
    /* 
        NoDelimiterCurrencyAtTheEnd price format has ISO 
          Euro value,
          fraction (the character between Euro and Cent),
          Cent value,
          minusSign
      */
    case 'Empiriecom':
    case 'NoDelimiterCurrencyAtTheEnd': {
      try {
        // Intl.Numberformat will simply throw if the currency or locale doesn't make sense
        const numberFormatParts = new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
        }).formatToParts(price / 100);

        const formattedCurrency = numberFormatParts.find(({ type }) => type === 'currency')!.value;

        return `${numberFormatParts
          .filter(
            ({ type }) =>
              (type === 'group' && priceFormat === 'Empiriecom') ||
              type === 'decimal' ||
              type === 'fraction' ||
              type === 'integer' ||
              type === 'minusSign',
          )
          .map(({ value: partValue }) => partValue)
          .join('')} ${formattedCurrency}`;
      } catch (error) {
        logger.error(
          { err: error, locale, currency },
          `unable to execute Intl.NumberFormat with locale ${locale} and currency ${currency}`,
        );
      }
      // if custom formatting fails, always return the default iso format
    }
    /* 
        Iso price format is using the whole ISO price formatting standard
      */
    // eslint-disable-next-line no-fallthrough
    case 'Iso':
    default: {
      return formatNumber(price / 100, { style: 'currency', currency });
    }
  }
};
